import { useApi, apiPost } from "@/lib/api";
import { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Coins, Check, Zap, TrendingDown } from "lucide-react";

export default function Savings() {
  const { data, isLoading, error, refetch } = useApi("savings-summary", "/savings/summary");
  const [busy, setBusy] = useState(null);
  if (error) return <ErrorState />;
  const items = data?.items || [];

  const applyOne = async (r) => {
    setBusy(r.id);
    try { await apiPost(`/recommendations/${r.id}/accept`); toast.success(`Applied ${r.id} — ${fmtCurrency(r.est_saving_usd)}/yr captured`); await refetch(); }
    catch { toast.error("Could not apply recommendation"); } finally { setBusy(null); }
  };
  const applyTop = async () => {
    setBusy("top");
    try { const res = await apiPost("/savings/apply-top", { n: 3 }); toast.success(`Applied ${res.applied.length} recommendations — ${fmtCurrency(res.applied_saving)}/yr captured`); await refetch(); }
    catch { toast.error("Autopilot failed"); } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader eyebrow="Navitra Ledger · Economics" title="Savings Autopilot" subtitle="AI-surfaced cost optimisations across every vendor. Review and apply with one click.">
        <Button size="sm" onClick={applyTop} disabled={busy === "top" || !items.some((r) => r.status !== "accepted")} data-testid="apply-top-btn"><Zap className="h-4 w-4 mr-1.5" />{busy === "top" ? "Applying…" : "Apply top 3"}</Button>
      </PageHeader>

      {isLoading ? <LoadingBlock className="h-40" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <Card><CardContent className="p-5"><p className="text-xs uppercase tracking-wide text-muted-foreground">Available savings</p><p className="font-display text-3xl text-navy mt-1" data-testid="savings-open">{fmtCurrency(data.open_total)}</p><p className="text-xs text-muted-foreground mt-1">annualised, open recommendations</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-xs uppercase tracking-wide text-muted-foreground">Captured</p><p className="font-display text-3xl text-sev-success mt-1" data-testid="savings-accepted">{fmtCurrency(data.accepted_total)}</p><p className="text-xs text-muted-foreground mt-1">applied via Autopilot</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-xs uppercase tracking-wide text-muted-foreground">Opportunities</p><p className="font-display text-3xl mt-1">{items.filter((r) => r.status !== "accepted").length}</p><p className="text-xs text-muted-foreground mt-1">of {items.length} tracked (R01–R12)</p></CardContent></Card>
          </div>

          <div className="space-y-2">
            {items.map((r) => {
              const done = r.status === "accepted";
              return (
                <div key={r.id} data-testid={`rec-${r.id}`} className="flex items-center gap-4 rounded-xl border bg-card p-4">
                  <span className="h-10 w-10 rounded-lg bg-sev-success/10 flex items-center justify-center shrink-0"><TrendingDown className="h-5 w-5 text-sev-success" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{r.recommendation}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.id} · {r.signal} · owner: {r.owner}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold mono text-sm">{r.est_saving_usd > 0 ? fmtCurrency(r.est_saving_usd) : "—"}</p>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.action}</p>
                  </div>
                  {done ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sev-success shrink-0 w-24 justify-center"><Check className="h-4 w-4" />Applied</span>
                  ) : (
                    <Button size="sm" variant="outline" className="shrink-0 w-24" disabled={busy === r.id || r.est_saving_usd === 0} onClick={() => applyOne(r)} data-testid={`apply-${r.id}`}>{busy === r.id ? "…" : "Apply"}</Button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
